/**
 * MobileNavMenu - Mobile hamburger menu navigation content
 *
 * Rendered inside the Header's mobile Sheet. Data-driven so the link list
 * stays in parity with the desktop sidebars:
 *   - Main app pages (Dashboard / Map / Reports / Records)
 *   - Records section (mirrors RecordsSidebar)
 *   - Settings sections (mirror SettingsSidebar: General / Organization / Platform)
 */

import { useLocation } from 'react-router-dom';
import {
  Download,
  LayoutDashboard,
  BellRing,
  Map,
  BarChart3,
  ClipboardList,
  Truck,
  Wrench,
  History,
  User,
  Bell,
  Building,
  Users,
  Film,
  QrCode,
  Building2,
  Globe,
  Layers,
  Zap,
  Cpu,
  Settings,
  Smartphone,
  Trash2,
  FolderOpen,
  AlertTriangle,
} from 'lucide-react';
import { useRBAC } from '@/contexts/RBACContext';
import { useNavigateWithParams } from '@/hooks/useNavigateWithParams';
import { useFilters } from '@/contexts/FilterContext';
import { TRAINING_NAV_ITEMS } from '@/pages/training/trainingNav';

interface NavItem {
  name: string;
  path: string;
  icon: React.ComponentType<{ className?: string }>;
  /** Custom active check; defaults to exact pathname match */
  isActive?: (pathname: string) => boolean;
}

// Main app navigation (top of menu, larger touch targets)
const mainItems: NavItem[] = [
  {
    name: 'Dashboard',
    path: '/dashboard',
    icon: LayoutDashboard,
    isActive: (p) => p === '/' || p === '/dashboard',
  },
  { name: 'Map', path: '/map', icon: Map },
  { name: 'Reports', path: '/reports', icon: BarChart3 },
  // Training's entry point is the account (user) dropdown, not the nav — but once
  // on a /training page the contextual sub-nav section below still appears here.
];

// Training section - mirrors TrainingSidebar (all signed-in users)
const trainingItems: NavItem[] = TRAINING_NAV_ITEMS.map((i) => ({
  name: i.name,
  path: i.path,
  icon: i.icon,
}));

// Records section - mirrors RecordsSidebar (internal users only)
const recordsItems: NavItem[] = [
  { name: 'Dashboard', path: '/records', icon: LayoutDashboard },
  { name: 'Unit Inventory', path: '/records/inventory', icon: Truck },
  { name: 'Files', path: '/records/files', icon: FolderOpen },
  { name: 'Maintenance', path: '/records/maintenance', icon: Wrench },
  { name: 'Work Orders', path: '/records/work-orders', icon: ClipboardList },
  { name: 'Damage', path: '/records/damage', icon: AlertTriangle },
  { name: 'Check-Ins', path: '/records/check-ins', icon: History },
];

// Settings > General - mirrors SettingsSidebar "General" section
const generalItems: NavItem[] = [
  { name: 'Overview', path: '/settings', icon: LayoutDashboard },
  { name: 'My Profile', path: '/settings/profile', icon: User },
  { name: 'Notifications', path: '/settings/notifications', icon: Bell },
];

// Settings > Organization - mirrors SettingsSidebar "Organization" section
const organizationItems: NavItem[] = [
  { name: 'Company', path: '/settings/company', icon: Building },
  { name: 'Users', path: '/settings/users', icon: Users },
  { name: 'Groups', path: '/settings/groups', icon: Film },
  { name: 'Fleet', path: '/settings/units', icon: Truck },
  { name: 'Audit Logs', path: '/settings/audit', icon: ClipboardList },
];

// QR Codes lives in the Organization section on desktop but is gated to
// platform.all (superadmin) there, so it gets the same gate here.
const qrCodesItem: NavItem = { name: 'QR Codes', path: '/settings/qr-codes', icon: QrCode };

// Settings > Platform - mirrors SettingsSidebar "Platform" section (superadmin)
const platformItems: NavItem[] = [
  { name: 'All Companies', path: '/settings/companies', icon: Building2 },
  { name: 'Global Audit', path: '/settings/global-audit', icon: Globe },
  { name: 'Metadata', path: '/settings/filters', icon: Layers },
  { name: 'Floorplans', path: '/settings/floorplans', icon: Map },
  { name: 'Electric Rates', path: '/settings/rates', icon: Zap },
  { name: 'System Devices', path: '/settings/devices', icon: Cpu },
  { name: 'System Preferences', path: '/settings/config', icon: Settings },
  { name: 'Pocket', path: '/settings/pocket', icon: Smartphone },
];

interface MobileNavMenuProps {
  /** Called after a link is tapped so the parent Sheet can close */
  onNavigate: () => void;
  /** Unread "new documents" count for the Downloads entry badge (client users). */
  downloadsUnreadCount?: number;
}

export function MobileNavMenu({ onNavigate, downloadsUnreadCount = 0 }: MobileNavMenuProps) {
  const location = useLocation();
  const navigate = useNavigateWithParams();
  const {
    loading: rbacLoading,
    isAdmin,
    isGroupAdmin,
    isCompanyAdmin,
    isSuperadmin,
    isInternal,
  } = useRBAC();
  const { flushCache } = useFilters();

  const isActiveRecords = location.pathname.startsWith('/records');
  const isActiveTraining = location.pathname.startsWith('/training');

  const renderItem = (item: NavItem, primary = false, badge = 0) => {
    const Icon = item.icon;
    const active = item.isActive
      ? item.isActive(location.pathname)
      : location.pathname === item.path;

    return (
      <button
        key={item.path}
        onClick={() => {
          navigate(item.path);
          onNavigate();
        }}
        className={`flex items-center gap-3 px-4 rounded-lg text-left transition-colors ${
          primary ? 'py-3' : 'py-2.5'
        } ${active ? 'bg-[#8FB560]/10 text-[#8FB560]' : 'hover:bg-accent'}`}
      >
        <Icon className={primary ? 'h-5 w-5' : 'h-4 w-4'} />
        <span className={primary ? 'font-medium' : 'text-sm'}>{item.name}</span>
        {badge > 0 && (
          <span
            className="ml-auto flex h-5 min-w-[20px] items-center justify-center rounded-full bg-error/80 px-1.5 text-[11px] font-bold text-white"
            aria-label={`${badge} new documents`}
          >
            {badge > 99 ? '99+' : badge}
          </span>
        )}
      </button>
    );
  };

  const sectionHeading = (title: string, withDivider = false) => (
    <>
      {withDivider && <div className="my-2 mx-4 border-t border-border" />}
      <div className={`px-4 py-2 ${withDivider ? '' : 'mt-2'}`}>
        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
          {title}
        </span>
      </div>
    </>
  );

  return (
    <nav className="flex flex-col p-2 flex-1 overflow-y-auto">
      {/* Main Navigation */}
      {mainItems.map((item) => renderItem(item, true))}
      {!isInternal &&
        renderItem(
          {
            name: 'Downloads',
            path: '/portal',
            icon: Download,
            isActive: (p) => p.startsWith('/portal'),
          },
          true,
          downloadsUnreadCount
        )}
      {isInternal &&
        renderItem(
          {
            name: 'Records',
            path: '/records',
            icon: ClipboardList,
            isActive: (p) => p.startsWith('/records'),
          },
          true
        )}
      {isInternal &&
        renderItem(
          {
            name: 'Alerts',
            path: '/alerts',
            icon: BellRing,
            isActive: (p) => p === '/alerts',
          },
          true
        )}

      {/* Records Section - Show when on /records pages */}
      {isActiveRecords && isInternal && (
        <>
          {sectionHeading('Records', true)}
          {recordsItems.map((item) => renderItem(item))}
        </>
      )}

      {/* Training Section - Show when on /training pages (all signed-in users) */}
      {isActiveTraining && (
        <>
          {sectionHeading('Training', true)}
          {trainingItems.map((item) => renderItem(item))}
        </>
      )}

      {/* General Settings Section - Show when NOT on a section with its own sub-nav */}
      {!rbacLoading && !isActiveRecords && !isActiveTraining && (
        <>
          {sectionHeading('General', true)}
          {generalItems.map((item) => renderItem(item))}
        </>
      )}

      {/* Organization Section - Show for admin, group_admin, or company_admin */}
      {!rbacLoading && (isAdmin || isGroupAdmin || isCompanyAdmin) && (
        <>
          {sectionHeading('Organization')}
          {organizationItems.map((item) => renderItem(item))}
          {isSuperadmin && renderItem(qrCodesItem)}
        </>
      )}

      {/* Platform Section - Only for Superadmins */}
      {!rbacLoading && isSuperadmin && (
        <>
          {sectionHeading('Platform')}
          {platformItems.map((item) => renderItem(item))}
        </>
      )}

      {/* Spacer to push Flush Cache to bottom */}
      <div className="flex-1" />

      {/* Divider before Flush Cache */}
      <div className="my-2 mx-4 border-t border-border" />

      {/* Flush Cache button - Always at bottom */}
      <button
        onClick={() => {
          flushCache();
          onNavigate();
          window.location.reload();
        }}
        className="flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors hover:bg-accent text-muted-foreground"
      >
        <Trash2 className="h-5 w-5" />
        <span className="font-medium">Flush Cache</span>
      </button>
    </nav>
  );
}
